import type { ArErrorCode, CompatibilityFailureCode } from "../types";

export interface CompatibilityEnvironment {
  isSecureContext: boolean;
  mediaDevices?: {
    getUserMedia?: typeof navigator.mediaDevices.getUserMedia;
  };
  document: Pick<Document, "createElement">;
  hasCanvasElement: boolean;
  hasPromise: boolean;
  hasWebGLRenderingContext: boolean;
  hasWebGL2RenderingContext: boolean;
}

export interface CompatibilityResult {
  ok: boolean;
  reason?: CompatibilityFailureCode;
}

export function createCompatibilityEnvironment(win: Window = window): CompatibilityEnvironment {
  const globalWindow = win as Window & typeof globalThis;

  return {
    isSecureContext: win.isSecureContext === true,
    mediaDevices: win.navigator.mediaDevices,
    document: win.document,
    hasCanvasElement: typeof globalWindow.HTMLCanvasElement !== "undefined",
    hasPromise: typeof globalWindow.Promise !== "undefined",
    hasWebGLRenderingContext: typeof globalWindow.WebGLRenderingContext !== "undefined",
    hasWebGL2RenderingContext: typeof globalWindow.WebGL2RenderingContext !== "undefined",
  };
}

export function hasUsableWebGL(env: CompatibilityEnvironment): boolean {
  if (!env.hasCanvasElement || (!env.hasWebGLRenderingContext && !env.hasWebGL2RenderingContext)) {
    return false;
  }

  const canvas = env.document.createElement("canvas");
  const getContext = canvas.getContext.bind(canvas);
  const context =
    getContext("webgl2", { failIfMajorPerformanceCaveat: true }) ??
    getContext("webgl", { failIfMajorPerformanceCaveat: true }) ??
    getContext("experimental-webgl", { failIfMajorPerformanceCaveat: true } as WebGLContextAttributes);

  return Boolean(context);
}

export function checkCompatibility(
  env: CompatibilityEnvironment = createCompatibilityEnvironment(),
): CompatibilityResult {
  if (!env.isSecureContext) {
    return { ok: false, reason: "INSECURE_CONTEXT" };
  }

  if (!env.mediaDevices?.getUserMedia) {
    return { ok: false, reason: "CAMERA_UNAVAILABLE" };
  }

  if (!env.hasPromise || !env.hasCanvasElement) {
    return { ok: false, reason: "UNSUPPORTED_BROWSER" };
  }

  if (!hasUsableWebGL(env)) {
    return { ok: false, reason: "WEBGL_UNAVAILABLE" };
  }

  return { ok: true };
}

export function compatibilityFailureToArErrorCode(reason: CompatibilityFailureCode): ArErrorCode {
  if (reason === "UNSUPPORTED_BROWSER") {
    return "WEBGL_UNAVAILABLE";
  }

  return reason;
}

export function mapCameraError(error: unknown): ArErrorCode {
  const name = getErrorName(error);

  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    return "PERMISSION_DENIED";
  }

  if (
    name === "NotReadableError" ||
    name === "TrackStartError" ||
    name === "AbortError" ||
    name === "NotReadableError"
  ) {
    return "CAMERA_BUSY";
  }

  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "OverconstrainedError" ||
    name === "ConstraintNotSatisfiedError"
  ) {
    return "CAMERA_UNAVAILABLE";
  }

  return "CAMERA_UNAVAILABLE";
}

function getErrorName(error: unknown): string {
  if (typeof error === "object" && error !== null && "name" in error) {
    return String((error as { name?: unknown }).name ?? "");
  }

  return "";
}
